import { FC, PropsWithChildren } from 'react';
import { cx } from '@emotion/css';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';

export type StarRating = 1 | 2 | 3 | 4 | 5;

const RATING_VALUES: StarRating[] = [1, 2, 3, 4, 5];
const starRatingIcon = getDesignSystemIcon('icon-star-16.svg');
const starRatingOutlineIcon = getDesignSystemIcon('icon-star-outline-16.svg');

interface StarRatingProps {
  value?: StarRating;
  className?: string;
  starSize?: number;
  iconClassName?: string;
  iconActiveClassName?: string;
  onSelect?: (ratingValue: StarRating) => void;
  dataTestId?: string;
}

export const StarRating: FC<PropsWithChildren<StarRatingProps>> = ({
  value = 0,
  className,
  starSize = 4,
  iconClassName = 'text-secondary-dark',
  iconActiveClassName = 'text-primary-orange',
  onSelect,
  dataTestId = 'starRating',
  children,
}) => {
  const iconForRatingValue = (ratingValue: StarRating) => (ratingValue <= value ? starRatingIcon : starRatingOutlineIcon);
  const classNameForRatingValue = (ratingValue: StarRating) => (ratingValue <= value ? iconActiveClassName : iconClassName);

  return (
    <div className={cx('flex items-center gap-x-3 text-secondary-dark stihl-text-body-default-reg', className)} data-testid={dataTestId}>
      {RATING_VALUES.map((ratingValue) => (
        <PhoenixIcon
          key={ratingValue}
          onClick={() => onSelect?.(ratingValue)}
          icon={iconForRatingValue(ratingValue)}
          className={cx(`w-${starSize} h-${starSize}`, classNameForRatingValue(ratingValue), onSelect && 'cursor-pointer')}
          dataTestId={`icon-rating-value-${ratingValue}`}
        />
      ))}
      {children}
    </div>
  );
};
